import { useState, useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import type { CascadingSelectValuePair } from '@atlassian/jira-issue-field-cascading-select-editview-full/src/ui/cascading-select/types.tsx';
import { CASCADING_SELECT_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import type {
	cascadingSelectField_Mutation as CascadingSelectMutation,
	cascadingSelectField_Mutation$rawResponse as CascadingSelectMutationResponse,
} from '@atlassian/jira-relay/src/__generated__/cascadingSelectField_Mutation.graphql';

export const useCascadingSelectField = ({
	fieldAriId,
	issueKey,
	fieldKey,
	initialValue,
	onFailure,
}: {
	fieldAriId: string;
	issueKey: string;
	fieldKey: string;
	initialValue: CascadingSelectValuePair | null;
	onFailure?: (error: Error) => void;
}) => {
	const [editValue, setEditValue] = useState<CascadingSelectValuePair | null>(initialValue);
	const [commit] = useMutation<CascadingSelectMutation>(graphql`
		mutation cascadingSelectField_Mutation($input: JiraUpdateCascadingSelectFieldInput!)
		@raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateCascadingSelectField(input: $input) {
					success
					errors {
						message
					}
				}
			}
		}
	`);

	const saveField = useCallback(
		(_iKey: string, _fKey: string, valuesForOperation: CascadingSelectValuePair | null) => {
			if (!valuesForOperation) return Promise.resolve(undefined);
			const { parentOptionValue, childOptionValue } = valuesForOperation;
			commit({
				variables: {
					input: {
						id: fieldAriId,
						operation: {
							operation: 'SET',
							parentOption: parentOptionValue?.id,
							childOption: childOptionValue?.id,
						},
					},
				},
				onError: onFailure,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				optimisticResponse: {
					jira: {
						updateCascadingSelectField: {
							success: true,
							errors: null,
						},
					},
				} as CascadingSelectMutationResponse,
			});
			return Promise.resolve(undefined);
		},
		[commit, fieldAriId, onFailure],
	);

	const [{ value }, { saveValue }] = useEditField<CascadingSelectValuePair | null, null>({
		fieldType: CASCADING_SELECT_CF_TYPE,
		issueId: fieldAriId,
		issueKey,
		initialValue,
		fieldKey,
		saveField,
	});

	return [
		{ value, editValue, setEditValue },
		{
			saveValue,
		},
	] as const;
};
